import React, { useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { ConfigProvider as AntdConfigProvider } from "antd";
import type { ConfigProviderProps } from "antd/es/config-provider";
import { MinimalModeProvider } from "../../shared/state/minimalModeContext";
import {
  isInPageContent,
  PAGE_POPUP_LAYER_CLASS,
} from "../Form/useScrollablePopupContainer";
import "./ConfigProvider.less";

export const ConfigProvider: React.FC<ConfigProviderProps> = ({
  children,
  ...props
}) => {
  // Antd mounts popups on document.body above everything, so a popup opened
  // from the page keeps painting over the page's sticky top bar and header
  // once its trigger has scrolled under them. Mounted in this layer instead,
  // it sits under them, as its trigger does. Not one opened from a layer above
  // them (a modal, drawer, popover...): that one goes on document.body.
  const pagePopupLayerRef = useRef<HTMLDivElement>(null);
  const getPopupContainer = useCallback((trigger?: HTMLElement) => {
    // A modal or drawer itself: antd asks with no trigger, or with
    // document.body. Returning nothing leaves it to antd's default container,
    // created as it opens, so the last one opened stays on top. Given
    // document.body itself, it would be portaled straight into it, in mount
    // order instead, which can put a newer modal under an older one.
    if (!trigger || trigger === document.body) {
      return null as unknown as HTMLElement;
    }
    return isInPageContent(trigger) && pagePopupLayerRef.current
      ? pagePopupLayerRef.current
      : document.body;
  }, []);

  return (
    <AntdConfigProvider getPopupContainer={getPopupContainer} {...props}>
      <MinimalModeProvider>{children}</MinimalModeProvider>
      {createPortal(
        <div className={PAGE_POPUP_LAYER_CLASS} ref={pagePopupLayerRef} />,
        document.body
      )}
    </AntdConfigProvider>
  );
};
